import { t as i18nT, TFunction } from 'i18next';
import { isMobilePhone } from 'validator';
import { z } from 'zod';

//@ts-ignore
z.setErrorMap((issue, ctx) => {
  switch (issue.code) {
    case 'invalid_type':
      return { message: i18nT('invalid_phone_number') };
    default:
      return { message: ctx.defaultError };
  }
});

export const registerSchema = (t: TFunction<'translation', undefined>) =>
  z.object({
    phoneNumber: z
      .string()
      .min(1, t('phone_number_required'))
      .transform((val) => val.replace(/\s/g, ''))
      .refine((val) => isMobilePhone(val, 'any'), {
        message: t('invalid_phone_number'),
      }),
    country_code: z.string().min(1, t('country_required')),
    email: z
      .string()
      .min(1, { message: t('email_required') })
      .email(t('invalid_email')),
    password: z
      .string()
      .min(8, t('password_min_length'))
      .regex(/[A-Z]/, t('password_uppercase_required'))
      .regex(/[a-z]/, t('password_lowercase_required'))
      .regex(/[0-9]/, t('password_number_required'))
      .regex(/[\W_]/, t('password_special_character_required')),
    terms: z.boolean().refine((value) => value === true, {
      message: t('terms_and_conditions_required'),
    }),
    notUSResidentOrTaxPayer: z.boolean().refine((value) => value === true, {
      message: t('us_resident_or_tax_payer_required'),
    }),
  });

export type RegisterSchemaType = z.infer<ReturnType<typeof registerSchema>>;

export const loginSchema = (t: TFunction<'translation', undefined>) =>
  z.object({
    email: z
      .string()
      .min(1, { message: t('email_required') })
      .email(t('invalid_email')),
    password: z.string().min(1, t('password_required')),
  });

export type LoginSchemaType = z.infer<ReturnType<typeof loginSchema>>;

export enum AuthFormTabs {
  REGISTER = 'register',
  LOGIN = 'login',
}

export interface VerifySessionRequestBodyToSign {
  source_service: string;
  timestamp: number;
  token: string;
}

export interface VerifySessionRequestBody extends VerifySessionRequestBodyToSign {
  signature: string;
}

export interface SessionResponse {
  session_id: string;
  token_type: string;
  access_expires_at?: number;
  access_token?: string;
  expires_at?: number;
  one_time_token?: string;
  refresh_expires_at?: number;
  refresh_token?: string;
}

export interface CreateSessionRequest {
  signature: string;
  source_service: string;
  timestamp: number;
  country?: string;
  email?: string;
  ip?: string;
  method?: string;
  password?: string;
  path?: string;
  payload_hash?: string;
  target_service?: string;
  user_agent?: string;
}
export interface VerifySessionResponse {
  expires_at: number;
  token_type: string;
  valid: boolean;
  entities?: string[];
  metadata?: Record<string, any>;
  user_id?: string;
}

export interface VerifySessionRequest {
  signature: string;
  source_service: string;
  timestamp: number;
  token: string;
  country?: string;
  method?: string;
  path?: string;
  user_agent?: string;
}

export interface UserAgentInfo {
  browser: string;
  browser_version: string;
  os: string;
  platform: string;
  raw: string;
}

interface ClientMetaData {
  b2c: number;
  country: string;
  email: string;
  ip: string;
  issued_at: number;
  issuer: string;
}

export interface ClientSessionMetadata {
  country?: string;
  ip?: string;
  is_current?: boolean;
}

export interface ClientSession {
  created_at: string;
  entity: string[];
  issuer_service: string;
  last_used: string;
  metadata: ClientSessionMetadata | null;
  session_id: string;
  user_id: string;
  user_agent?: UserAgentInfo;
}

export interface JWTClaims {
  expires_at: number;
  issued_at: number;
  type: string;
  entities?: string[];
  metadata?: ClientMetaData;
  source_service?: string;
  target_service?: string;
  user_id?: string;
}

export interface CommonFields {
  source_service: string;
  timestamp: number;
  signature?: string;
}

export interface ServiceTokenRequest {
  method: string;
  path: string;
  signature: string;
  source_service: string;
  target_service: string;
  timestamp: number;
  payload_hash?: string;
}

export interface AddressData {
  id: number;
  address: string;
  city: string;
  clientId: number;
  country: CountryDetails;
  postcode: string;
  state: string;
  type: string;
  createTime?: string | null;
  updateTime?: string | null;
}

interface CountryDetails {
  alpha2Code?: string | null;
  alpha3Code?: string | null;
  countryId?: number | null;
  countryName?: string | null;
  numericCode?: string | null;
}

interface CompanyDetails {
  fullName?: string | null;
  shortName?: string | null;
}

interface VerificationDetails {
  id?: number;
  name?: string;
}

interface AccountDetails {
  accountId: number;
  accountNumber: number;
  caption: string;
  createTime: string;
}

export enum OnboardingSteps {
  MISSING_VERIFICATION = 1,
  MISSING_BASIC_INFORMATION = 2,
  MISSING_ADDRESS_INFORMATION = 3,
}

type MT5AccountAccount = {
  id: number;
  description: string;
};

export interface ClientSummary {
  id: number;
  city: string;
  email: string;
  first_name: string;
  internal_type: string;
  last_name: string;
  middle_name: string;
  name: string;
  phone: string;
  status: string;
  accounts?: AccountDetails[];
  addresses?: AddressData;
  birthday?: string | null;
  company?: CompanyDetails | null;
  country?: CountryDetails | null;
  createdAt?: Date | null;
  mt5_accounts?: MT5AccountAccount[];
  onboarding_steps?: OnboardingSteps[];
  verification_level?: VerificationDetails | null;
}

export interface EnhancedClientSummaryResponse {
  addresses?: AddressData[];
  client_details?: ClientSummary;
}

export interface ClientUpdateRequest {
  birthday: string;
  first_name: string;
  last_name: string;
  company?: string;
  country_code?: number;
  email?: string;
  entity?: string;
  gender?: number;
  locale?: string;
  middle_name?: string;
  nickname?: string;
  phone?: string;
}

export interface AddressUpdateRequest {
  address: string;
  city: string;
  postcode: string;
  state: string;
  country_code?: number;
  type?: string;
}

interface AddressType {
  address: string;
  city: string;
  country_code: string;
  postal_code: string;
  state: string;
}

interface PhoneType {
  phone: string;
}

export interface RegisterClientRequest {
  addresses: AddressType[];
  email: string;
  info: {
    givenName: string;
    familyName: string;
    birthday: string;
  };
  passwordHash: string;
  phones: PhoneType[];
}
